import { HTMLAttributeAnchorTarget } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled, { css, ThemeProps, DefaultTheme } from 'styled-components';
import { Open16 } from 'ui/icons/aurora/16px/Open16';
import { link2, link, captionLink, spacing, WebLink2, colors, monochromeLink } from 'ui/theme';

export const LINK_TEST_ID = 'link';
export const ROUTER_LINK_TEST_ID = 'react-router-link';

export const sharedLinkCSS = css`
  display: inline-block;
`;

type LinkVariants = 'default' | 'inherit' | 'monochrome';

export interface StyledLinkProps extends ThemeProps<DefaultTheme> {
  size?: 'body2' | 'caption' | 'body1';
  variant?: LinkVariants;
  $inTable?: boolean;
}

const colorVariants = css<StyledLinkProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'monochrome':
        return css`
          ${monochromeLink}
        `;
      case 'inherit':
        return css`
          color: inherit;
          text-decoration-color: inherit;
        `;
      default:
        return css`
          ${sharedLinkCSS};
        `;
    }
  }}
`;

const StyledOpenIcon = styled(Open16)`
  margin-left: ${spacing.quarter};
  vertical-align: middle;
`;

const sizeStyles = css<StyledLinkProps>`
  ${({ size }) => {
    switch (size) {
      case 'caption':
        return captionLink;
      case 'body1':
        return link; // Apply new size styles
      default:
        return link2;
    }
  }}
`;

const tableLinkStyles = css<StyledLinkProps>`
  ${({ $inTable }) =>
    $inTable &&
    css`
      color: ${colors.textPrimary};
      text-decoration-color: transparent;
      font-weight: 400;
      word-break: break-word;
    `}
`;

export const StyledLink = styled(WebLink2)<StyledLinkProps>`
  ${sizeStyles}
  ${colorVariants}
  ${tableLinkStyles}
  ${sharedLinkCSS}
`;

export const StyledRouterLink = styled(RouterLink)<StyledLinkProps>`
  ${sizeStyles}
  ${colorVariants}
  ${tableLinkStyles}
  ${sharedLinkCSS}
`;

export type LinkProps = {
  /** Determines the type styles of the link */
  size?: 'body2' | 'caption' | 'body1';
  /** Determines the color of the link */
  variant?: LinkVariants;
  /** Displayed link text */
  children: React.ReactNode;
  /** URL that link points to */
  href: string;
  /** Where to display the linked URL */
  target?: HTMLAttributeAnchorTarget;
  /** Relationship between current doc and linked doc */
  rel?: string;
  /** Whether to display the new tab icon */
  hasOpenNewTabIcon?: boolean;
  /** Whether the link is served via react-router */
  useReactRouter?: boolean;
  /** Whether the link is used within an index table */
  inTable?: boolean;
};

export const Link = ({
  href,
  children,
  hasOpenNewTabIcon = false,
  useReactRouter = false,
  size = 'body2',
  variant = 'default',
  inTable = false,
  ...props
}: LinkProps) => {
  let iconSize;
  switch (size) {
    case 'caption':
      iconSize = 12;
      break;
    case 'body1':
      iconSize = 16; // New icon size for 'webLink2'
      break;
    default:
      iconSize = 14; // Default case for 'link2'
  }

  const linkContent = (
    <>
      {children}
      {hasOpenNewTabIcon && <StyledOpenIcon height={iconSize} width={iconSize} isColorInherited />}
    </>
  );

  return useReactRouter ? (
    <StyledRouterLink
      data-test={ROUTER_LINK_TEST_ID}
      to={href}
      size={size}
      variant={variant}
      $inTable={inTable}
      {...props}
    >
      {linkContent}
    </StyledRouterLink>
  ) : (
    <StyledLink
      data-test={LINK_TEST_ID}
      href={href}
      size={size}
      variant={variant}
      $inTable={inTable}
      {...props}
    >
      {linkContent}
    </StyledLink>
  );
};
